import React from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { TrendingUp, DollarSign, Users, Clock, Package, Award, Target, AlertCircle, CheckCircle2, Lightbulb } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { appointmentService } from '../services/appointmentService';
import { parseYmd } from '../shared/dateUtils';

interface BPIBreakdown {
  serviceCount: number;
  revenue: number;
  speed: number;
  satisfaction: number;
  rebooking: number;
}

interface ChartProps {
  barberName: string;
  barberId?: string;
  ytdRevenue: number;
  ytdClients: number;
  monthlyRevenue: number;
  monthlyClients: number;
  bpiBreakdown?: BPIBreakdown;
  bpiScore?: number;
}

const BarberPerformanceCharts: React.FC<ChartProps> = ({
  barberName,
  barberId,
  ytdRevenue,
  ytdClients,
  monthlyRevenue,
  monthlyClients,
  bpiBreakdown,
  bpiScore = null as any // was 85 (a fake default shown when no real BPI exists — bpiData is empty). Audit fix.
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Theme-aware colors for charts
  const chartColors = {
    grid: isDark ? '#374151' : '#E5E7EB',
    axis: isDark ? '#9CA3AF' : '#6B7280',
    text: isDark ? '#E5E7EB' : '#374151',
    tooltipBg: isDark ? '#1F2937' : '#FFFFFF',
    tooltipText: isDark ? '#F9FAFB' : '#1F2937',
    tooltipBorder: isDark ? '#374151' : '#E5E7EB',
  };

  // REAL chart data from this barber's appointments (2026-08-02). Empty until bookings exist.
  const _pa = barberId ? appointmentService.getAppointmentsByBarber(barberId) : [];
  const _pdone = _pa.filter(a => a.status === 'completed' || a.status === 'paid');
  const _mNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const _dNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const _palette = ['#8B5CF6', '#6366F1', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];

  // Monthly trend — real revenue + distinct clients per month, last 6 calendar months
  const _base = new Date();
  const monthlyTrendData = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(_base.getFullYear(), _base.getMonth() - (5 - i), 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const rows = _pdone.filter(a => (a.date || '').slice(0, 7) === key);
    return { month: _mNames[d.getMonth()], revenue: rows.reduce((s, a) => s + (a.price || 0), 0), clients: new Set(rows.map(a => a.clientId)).size };
  });

  // Service mix — real, grouped by service from completed/paid appointments
  const _svcCount: Record<string, number> = {};
  _pdone.forEach(a => { _svcCount[a.service] = (_svcCount[a.service] || 0) + 1; });
  const serviceBreakdown = Object.entries(_svcCount)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value], i) => ({ name, value, color: _palette[i % _palette.length] }));

  // Weekly pattern — real, by day of week
  const weeklyPattern = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
    const rows = _pdone.filter(a => _dNames[parseYmd(a.date).getDay()] === day); // parseYmd = LOCAL weekday (bare new Date(ymd) buckets to the wrong day in ET)
    return { day, clients: rows.length, revenue: rows.reduce((s, a) => s + (a.price || 0), 0) };
  });

  // BPI Performance Breakdown - 5 core metrics
  const bpiMetricsData = bpiBreakdown ? [
    { metric: 'Service Count', value: bpiBreakdown.serviceCount, weight: '25%' },
    { metric: 'Revenue', value: bpiBreakdown.revenue, weight: '25%' },
    { metric: 'Speed', value: bpiBreakdown.speed, weight: '20%' },
    { metric: 'Satisfaction', value: bpiBreakdown.satisfaction, weight: '20%' },
    { metric: 'Rebooking', value: bpiBreakdown.rebooking, weight: '10%' }
  ] : [
    { metric: 'Service Count', value: 85, weight: '25%' },
    { metric: 'Revenue', value: 88, weight: '25%' },
    { metric: 'Speed', value: 82, weight: '20%' },
    { metric: 'Satisfaction', value: 91, weight: '20%' },
    { metric: 'Rebooking', value: 87, weight: '10%' }
  ];

  // AI Recommendations based on BPI metrics
  const generateRecommendations = () => {
    const recommendations = [];
    const metrics = bpiMetricsData;

    // Find lowest 2 metrics
    const sortedMetrics = [...metrics].sort((a, b) => a.value - b.value);
    const weakest = sortedMetrics[0];
    const secondWeakest = sortedMetrics[1];

    // Generate recommendations
    if (weakest.value < 70) {
      recommendations.push({
        priority: 'high',
        icon: AlertCircle,
        title: `Improve ${weakest.metric}`,
        description: `Current score: ${weakest.value}/100. This metric needs immediate attention.`,
        action: getActionForMetric(weakest.metric)
      });
    }

    if (secondWeakest.value < 80) {
      recommendations.push({
        priority: 'medium',
        icon: Target,
        title: `Focus on ${secondWeakest.metric}`,
        description: `Score: ${secondWeakest.value}/100. Room for improvement.`,
        action: getActionForMetric(secondWeakest.metric)
      });
    }

    // Find strengths
    const strongest = sortedMetrics[sortedMetrics.length - 1];
    if (strongest.value >= 90) {
      recommendations.push({
        priority: 'low',
        icon: CheckCircle2,
        title: `Excellent ${strongest.metric}`,
        description: `Score: ${strongest.value}/100. Keep up the great work!`,
        action: `Maintain current ${strongest.metric.toLowerCase()} performance`
      });
    }

    return recommendations;
  };

  const getActionForMetric = (metric: string): string => {
    const actions: Record<string, string> = {
      'Service Count': 'Increase booking capacity during peak hours',
      'Revenue': 'Focus on upselling premium services and products',
      'Speed': 'Optimize service workflow and reduce downtime between appointments',
      'Satisfaction': 'Follow up with recent clients and request feedback',
      'Rebooking': 'Encourage clients to book their next appointment before leaving'
    };
    return actions[metric] || 'Review performance metrics and set improvement goals';
  };

  const recommendations = generateRecommendations();

  // Hourly distribution
  const hourlyDistribution = [
    { hour: '9AM', clients: 8 },
    { hour: '10AM', clients: 12 },
    { hour: '11AM', clients: 15 },
    { hour: '12PM', clients: 14 },
    { hour: '1PM', clients: 10 },
    { hour: '2PM', clients: 16 },
    { hour: '3PM', clients: 18 },
    { hour: '4PM', clients: 20 },
    { hour: '5PM', clients: 22 },
    { hour: '6PM', clients: 19 },
    { hour: '7PM', clients: 12 }
  ];

  return (
    <div className="space-y-8">
      {/* Monthly Revenue & Client Trends */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Monthly Performance Trends</h3>
          <TrendingUp className="h-6 w-6 text-green-500 dark:text-green-400" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="month" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <YAxis yAxisId="left" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <YAxis yAxisId="right" orientation="right" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <Tooltip
              contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '8px' }}
              labelStyle={{ color: chartColors.tooltipText }}
              itemStyle={{ color: chartColors.tooltipText }}
            />
            <Legend wrapperStyle={{ color: chartColors.text }} />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="#8B5CF6"
              strokeWidth={3}
              dot={{ fill: '#8B5CF6', r: 6 }}
              name="Revenue ($)"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="clients"
              stroke="#10B981"
              strokeWidth={3}
              dot={{ fill: '#10B981', r: 6 }}
              name="Clients"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Category Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Service Mix</h3>
            <Package className="h-5 w-5 text-purple-500 dark:text-purple-400" />
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={serviceBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {serviceBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* BPI Performance Breakdown Radar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">BPI Performance Breakdown</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Overall BPI Score: <span className="font-bold text-purple-600 dark:text-purple-400">{bpiScore != null ? `${bpiScore}/100` : '— not scored yet'}</span>
              </p>
            </div>
            <Award className="h-6 w-6 text-purple-500 dark:text-purple-400" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={bpiMetricsData}>
              <PolarGrid stroke={chartColors.grid} />
              <PolarAngleAxis
                dataKey="metric"
                stroke={chartColors.axis}
                tick={{ fill: chartColors.text, fontSize: 12 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                stroke={chartColors.axis}
                tick={{ fill: chartColors.text }}
              />
              <Radar
                name="Performance"
                dataKey="value"
                stroke="#8B5CF6"
                fill="#8B5CF6"
                fillOpacity={0.5}
                strokeWidth={3}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: chartColors.tooltipBg,
                  border: `1px solid ${chartColors.tooltipBorder}`,
                  borderRadius: '8px',
                  color: chartColors.tooltipText
                }}
                formatter={(value: number, name: string, props: any) => [
                  `${value}/100 (${props.payload.weight} weight)`,
                  name
                ]}
              />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-5 gap-2 text-xs text-center">
            {bpiMetricsData.map((metric, idx) => (
              <div key={idx} className="p-2 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="font-semibold text-gray-900 dark:text-white">{metric.value}</div>
                <div className="text-gray-500 dark:text-gray-400">{metric.weight}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weekly Pattern */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Weekly Performance Pattern</h3>
          <DollarSign className="h-6 w-6 text-green-500 dark:text-green-400" />
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={weeklyPattern}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="day" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <YAxis yAxisId="left" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <YAxis yAxisId="right" orientation="right" stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <Tooltip
              contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '8px' }}
              labelStyle={{ color: chartColors.tooltipText }}
              itemStyle={{ color: chartColors.tooltipText }}
            />
            <Legend wrapperStyle={{ color: chartColors.text }} />
            <Bar yAxisId="left" dataKey="revenue" fill="#8B5CF6" name="Revenue ($)" radius={[8, 8, 0, 0]} />
            <Bar yAxisId="right" dataKey="clients" fill="#10B981" name="Clients" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Hourly Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Peak Hours Analysis</h3>
          <Clock className="h-6 w-6 text-blue-500 dark:text-blue-400" />
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hourlyDistribution}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
            <XAxis dataKey="hour" stroke={chartColors.axis} tick={{ fill: chartColors.text }} angle={-45} textAnchor="end" height={60} />
            <YAxis stroke={chartColors.axis} tick={{ fill: chartColors.text }} />
            <Tooltip
              contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.tooltipBorder}`, borderRadius: '8px' }}
              labelStyle={{ color: chartColors.tooltipText }}
              itemStyle={{ color: chartColors.tooltipText }}
            />
            <Bar
              dataKey="clients"
              fill="#6366F1"
              name="Clients per Hour"
              radius={[8, 8, 0, 0]}
            >
              {hourlyDistribution.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.clients > 18 ? '#10B981' : entry.clients > 12 ? '#F59E0B' : '#6366F1'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">Peak Hours</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">Moderate</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">Low Activity</span>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl shadow-lg p-6 border-2 border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Lightbulb className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">AI Performance Recommendations</h3>
            </div>
            <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
              {recommendations.length} Insights
            </span>
          </div>

          <div className="space-y-4">
            {recommendations.map((rec, idx) => {
              const Icon = rec.icon;
              const priorityColors = {
                high: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200',
                medium: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
                low: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-800 text-green-800 dark:text-green-200'
              };

              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-2 ${priorityColors[rec.priority as keyof typeof priorityColors]}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-6 w-6 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-base">{rec.title}</h4>
                        <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-white/50 dark:bg-black/20">
                          {rec.priority} priority
                        </span>
                      </div>
                      <p className="text-sm mb-2 opacity-90">{rec.description}</p>
                      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-current/20">
                        <Target className="h-4 w-4 mt-0.5" />
                        <p className="text-sm font-medium">
                          <span className="opacity-90">Action:</span> {rec.action}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-white/50 dark:bg-black/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Pro Tip:</strong> Focus on addressing high-priority recommendations first for maximum BPI improvement.
                Review your progress weekly and adjust your strategies based on performance trends.
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-4 text-white">
          <Users className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{ytdClients}</p>
          <p className="text-sm opacity-90">YTD Clients</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-xl p-4 text-white">
          <DollarSign className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">${(ytdRevenue / 1000).toFixed(1)}K</p>
          <p className="text-sm opacity-90">YTD Revenue</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-4 text-white">
          <TrendingUp className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">+15%</p>
          <p className="text-sm opacity-90">Growth Rate</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl p-4 text-white">
          <Award className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">4.9</p>
          <p className="text-sm opacity-90">Client Rating</p>
        </div>
      </div>
    </div>
  );
};

export default BarberPerformanceCharts;